/**
 * Extração de eventos detalhados para drill-down de métricas (tiles clicáveis).
 *
 * Privacidade: usa apenas AnonymousId. Nenhum CPF/nome circula aqui.
 * Os eventos são ordenados por data desc (mais recente primeiro).
 */

import type { DiagnosticResult } from '$core/diagnostic.types';
import type { DiagnosticAssumptions, DailyJourney, LocalDate } from '$core/domain/canonical.types';
import type { AnonymousId } from '$core/privacy/pii.types';
import { buildDailyJourneys, inferEmployeeDailyMinutes } from './journeys';
import { detectShortJourney } from './journeys';
import { weekdayOf } from '$core/domain/dates';
import { computeInferredAbsenceTuples, computeCompanyWorkDays } from './absenceMetrics';

// =========================================================================
// Tipo público
// =========================================================================

export interface DrillDownEvent {
  employeeId: AnonymousId;
  /** Rótulo legível gerado por índice no array de colaboradores (ex.: "Colaborador #3"). */
  employeeLabel: string;
  date?: string;
  dateTime?: string;
  minutes?: number;
  reason?: string;
  [extra: string]: unknown;
}

// =========================================================================
// Helpers internos
// =========================================================================

/** Mapeia AnonymousId → "Colaborador #N" com base na ordem do dataset.employees. */
function buildEmployeeLabels(result: DiagnosticResult): Map<AnonymousId, string> {
  const map = new Map<AnonymousId, string>();
  result.dataset.employees.forEach((e, idx) => {
    map.set(e.identity.id, `Colaborador #${idx + 1}`);
  });
  return map;
}

function labelFor(id: AnonymousId, labels: Map<AnonymousId, string>): string {
  return labels.get(id) ?? `Colaborador ${id.slice(0, 8)}`;
}

/** Compara datas/dateTimes para ordenação descendente (mais recente primeiro). */
function sortDesc(a: DrillDownEvent, b: DrillDownEvent): number {
  const aKey = (a.dateTime ?? a.date ?? '');
  const bKey = (b.dateTime ?? b.date ?? '');
  if (bKey > aKey) return 1;
  if (bKey < aKey) return -1;
  return 0;
}

// =========================================================================
// Extratores por metricId
// =========================================================================

function extractShortJourneys(
  result: DiagnosticResult,
  assumptions: DiagnosticAssumptions,
  labels: Map<AnonymousId, string>,
): DrillDownEvent[] {
  const journeys = ensureJourneys(result, assumptions);
  const events: DrillDownEvent[] = [];

  for (const journey of journeys) {
    const delta = detectShortJourney(journey, assumptions.toleranceMinutes);
    if (delta === null) continue;
    events.push({
      employeeId: journey.employeeId,
      employeeLabel: labelFor(journey.employeeId, labels),
      date: journey.operationalDate,
      minutes: delta.minutes,
      expectedMinutes: journey.expectedMinutes,
      workedMinutes: journey.workedMinutes,
    });
  }

  return events.sort(sortDesc);
}

function extractFaltas(
  result: DiagnosticResult,
  assumptions: DiagnosticAssumptions,
  labels: Map<AnonymousId, string>,
): DrillDownEvent[] {
  const journeys = ensureJourneys(result, assumptions);
  const tuples = computeInferredAbsenceTuples(result.dataset, journeys);
  if (tuples === null) return [];

  const dailyHours = assumptions.dailyHours > 0 ? assumptions.dailyHours : 8;
  const inferredMinutes = dailyHours * 60;

  return tuples
    .map(t => ({
      employeeId: t.employeeId,
      employeeLabel: labelFor(t.employeeId, labels),
      date: t.date,
      days: 1,
      minutes: inferredMinutes,
      source: 'inferida',
    }))
    .sort(sortDesc);
}

// =========================================================================
// Extratores para métricas de Custo
// =========================================================================

/**
 * Excesso de jornada (todas as jornadas com workedMinutes > expectedMinutes).
 * Exclui jornadas com pares incompletos (não calculável).
 */
function extractCostExcessMinutes(
  result: DiagnosticResult,
  assumptions: DiagnosticAssumptions,
  labels: Map<AnonymousId, string>,
): DrillDownEvent[] {
  const journeys = ensureJourneys(result, assumptions);
  return journeys
    .filter(j => {
      if (j.pairs.some(p => p.imbalanced)) return false;
      return j.workedMinutes > j.expectedMinutes;
    })
    .map(j => ({
      employeeId: j.employeeId,
      employeeLabel: labelFor(j.employeeId, labels),
      date: j.operationalDate,
      minutes: j.workedMinutes - j.expectedMinutes,
      expectedMinutes: j.expectedMinutes,
      workedMinutes: j.workedMinutes,
    }))
    .sort(sortDesc);
}

/**
 * Atrasos com déficit (jornadas com workedMinutes < expectedMinutes além da
 * tolerância). Exclui jornadas com pares incompletos ou dados parciais.
 */
function extractCostAtrasos(
  result: DiagnosticResult,
  assumptions: DiagnosticAssumptions,
  labels: Map<AnonymousId, string>,
): DrillDownEvent[] {
  const journeys = ensureJourneys(result, assumptions);
  const tolerance = assumptions.toleranceMinutes ?? 0;
  return journeys
    .filter(j => {
      if (!j.scheduledDay || j.workedMinutes === 0) return false;
      if (j.pairs.some(p => p.imbalanced)) return false;
      // Dados parciais: déficit reflete batidas faltando, não atraso real.
      if (j.incompleteData) return false;
      return (j.expectedMinutes - j.workedMinutes) > tolerance;
    })
    .map(j => ({
      employeeId: j.employeeId,
      employeeLabel: labelFor(j.employeeId, labels),
      date: j.operationalDate,
      minutes: j.expectedMinutes - j.workedMinutes,
      expectedMinutes: j.expectedMinutes,
      workedMinutes: j.workedMinutes,
    }))
    .sort(sortDesc);
}

/**
 * Faltas inferidas pela heurística "dia de trabalho da empresa".
 * Reusa `computeInferredAbsenceTuples` da absenceMetrics — mesma fonte de
 * verdade da métrica agregada.
 */
function extractCostAbsences(
  result: DiagnosticResult,
  assumptions: DiagnosticAssumptions,
  labels: Map<AnonymousId, string>,
): DrillDownEvent[] {
  const events: DrillDownEvent[] = [];

  // Faltas inferidas (mesma heurística da métrica agregada).
  const journeys = ensureJourneys(result, assumptions);
  const tuples = computeInferredAbsenceTuples(result.dataset, journeys);
  if (tuples !== null) {
    const dailyHours = assumptions.dailyHours > 0 ? assumptions.dailyHours : 8;
    const inferredMinutes = dailyHours * 60;
    for (const t of tuples) {
      events.push({
        employeeId: t.employeeId,
        employeeLabel: labelFor(t.employeeId, labels),
        date: t.date,
        days: 1,
        minutes: inferredMinutes,
        source: 'inferida',
      });
    }
  }

  return events.sort(sortDesc);
}

// =========================================================================
// Utilitário: obtém jornadas (evita recalcular se disponíveis no futuro)
// =========================================================================

/** Reconstrói jornadas a partir do dataset + assumptions. */
function ensureJourneys(
  result: DiagnosticResult,
  assumptions: DiagnosticAssumptions,
): DailyJourney[] {
  return buildDailyJourneys(result.dataset, assumptions);
}

// =========================================================================
// Roteador público
// =========================================================================

/**
 * Retorna lista de eventos para o metricId solicitado, pronta para renderizar
 * numa tabela de auditoria. Array vazio quando não há dados ou metricId inválido.
 */
export function getDrillDownEvents(
  result: DiagnosticResult,
  metricId: string,
  assumptions: DiagnosticAssumptions,
): DrillDownEvent[] {
  const labels = buildEmployeeLabels(result);

  switch (metricId) {
    case 'absent.short_journeys':
      return extractShortJourneys(result, assumptions, labels);
    case 'absent.faltas':
      return extractFaltas(result, assumptions, labels);
    case 'cost.excess_minutes':
    case 'cost.cost_overtime':
      return extractCostExcessMinutes(result, assumptions, labels);
    case 'cost.cost_atrasos':
      return extractCostAtrasos(result, assumptions, labels);
    case 'cost.cost_absences':
      return extractCostAbsences(result, assumptions, labels);
    default:
      return [];
  }
}

// =========================================================================
// Ranking de colaboradores e breakdown diário
// =========================================================================

/**
 * Nomes dos dias da semana em português (índice 0 = domingo).
 */
const WEEKDAY_NAMES_PT = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
] as const;

/**
 * Gera o displayName de um colaborador para exibição LOCAL no dashboard.
 *
 * Hierarquia:
 * 1. `displayName` explícito (campo opcional, ainda não preenchido pelo
 *    pipeline — futura integração com complementaryData).
 * 2. `rawAnchor`: valor bruto do CPF/PIS/matrícula preservado pelo
 *    normalize. Mostrado direto pra ajudar o consultor a identificar
 *    o colaborador na sua máquina local. NÃO vaza pra exportações
 *    (que usam apenas o `id` hashado).
 * 3. Fallback: "Colaborador #N (ID *****1234)" usando últimos 4 chars
 *    do hash anônimo.
 */
function buildDisplayName(
  employee: DiagnosticResult['dataset']['employees'][number],
  idx: number,
): string {
  if (employee.displayName) return employee.displayName;
  const raw = employee.identity.rawAnchor;
  if (raw !== undefined && raw !== '') {
    const anchorLabel =
      employee.identity.anchor === 'cpf' ? 'CPF'
      : employee.identity.anchor === 'pis' ? 'PIS'
      : employee.identity.anchor === 'registration' ? 'Mat'
      : 'ID';
    return `Colaborador #${idx + 1} (${anchorLabel}: ${raw})`;
  }
  const last4 = employee.identity.id.slice(-4);
  return `Colaborador #${idx + 1} (ID *****${last4})`;
}

export interface EmployeeRanking {
  employeeId: AnonymousId;
  /** Nome legível: "Colaborador #N (ID *****1234)" ou displayName quando disponível. */
  displayName: string;
  /** Número de eventos/ocorrências contados para esta métrica. */
  eventCount: number;
  /** Número de dias com jornada registrada no período (dias que têm pelo menos uma marcação). */
  daysConsidered: number;
  /** Valor total que determina a ordenação (ex: soma minutos déficit, ou contagem de eventos). */
  totalValue: number;
  /** Rótulo descritivo do totalValue para exibição. */
  totalLabel: string;
}

/**
 * Tipo de linha do calendário diário do colaborador.
 *
 * - `punched`: dia com marcação (linha tradicional com entrada/pausa/saída).
 * - `aej-absence`: ausência registrada no AEJ (kind=absence).
 * - `inferred-falta`: dia-de-trabalho-da-empresa sem marcação e dentro da
 *   janela de presença do colaborador (entre primeira e última marcação).
 * - `folga`: dia sem marcação fora da heurística — fim de semana, feriado
 *   nacional, ou dia em que <50% da empresa trabalhou.
 */
export type DayKind = 'punched' | 'aej-absence' | 'inferred-falta' | 'folga';

export interface DailyBreakdown {
  date: LocalDate;
  /**
   * Chave única da sessão (mesmo dia pode ter mais de uma sessão quando
   * há split de turnos, ex.: manhã + noite com gap > 12h). Combina data +
   * timestamp ISO da primeira marcação para deduplicar. Útil para keyed
   * `{#each}` no Svelte — usar só `date` quebra o render quando há split.
   */
  sessionKey: string;
  /** Nome do dia da semana em português. */
  weekday: string;
  /** Classificação do dia. Para drill-downs não-absence sempre é 'punched'. */
  kind: DayKind;
  /**
   * Marcações do dia classificadas por posição no array ordenado por horário.
   * Índice 0=entrada, 1=pausa, 2=retorno, 3=saída, demais=extra.
   * Essa convenção casa com a estrutura típica de 4 batidas seg-sex.
   * Vazio para linhas non-`punched`.
   */
  punches: { time: string; kind: 'entrada' | 'pausa' | 'retorno' | 'saida' | 'extra' }[];
  expectedMin: number;
  workedMin: number;
  /**
   * Saldo do dia: `workedMin - expectedMin`. Positivo = hora extra
   * (trabalhou além do esperado); negativo = déficit (trabalhou menos
   * que o esperado). `null` quando o dia tem marcações ímpares ou dados
   * parciais — não dá pra inferir o que faltou e qualquer comparação
   * seria especulativa.
   */
  saldoMin: number | null;
  hasIssue: boolean;
  /** True quando o dia tem ao menos um par incompleto (marcações ímpares). */
  hasIncompletePunches: boolean;
  /**
   * True quando o padrão de marcações sugere dados incompletos (batidas faltando).
   * O déficit não é contabilizado como atraso — provavelmente faltam batidas de
   * retorno/saída ou há um segundo turno não registrado.
   */
  incompleteData: boolean;
  issues: string[];
}

/**
 * Classifica o tipo de uma marcação pelo seu índice posicional no array ordenado do dia.
 * Segue a convenção de 4 batidas típicas (entrada/pausa/retorno/saída).
 */
function classifyPunchKind(
  idx: number,
): 'entrada' | 'pausa' | 'retorno' | 'saida' | 'extra' {
  switch (idx) {
    case 0: return 'entrada';
    case 1: return 'pausa';
    case 2: return 'retorno';
    case 3: return 'saida';
    default: return 'extra';
  }
}

/**
 * Retorna array agregado por colaborador para a métrica solicitada,
 * ordenado por totalValue desc (quem mais tem vem primeiro).
 *
 * Inclui apenas colaboradores que tiveram pelo menos 1 ocorrência.
 */
export function getDrillDownRanking(
  result: DiagnosticResult,
  metricId: string,
  assumptions: DiagnosticAssumptions,
): EmployeeRanking[] {
  const events = getDrillDownEvents(result, metricId, assumptions);
  if (events.length === 0) return [];

  const employeeMap = new Map<AnonymousId, { count: number; totalMinutes: number }>();

  for (const ev of events) {
    const existing = employeeMap.get(ev.employeeId);
    if (existing) {
      existing.count += 1;
      existing.totalMinutes += typeof ev.minutes === 'number' ? ev.minutes : 0;
    } else {
      employeeMap.set(ev.employeeId, {
        count: 1,
        totalMinutes: typeof ev.minutes === 'number' ? ev.minutes : 0,
      });
    }
  }

  // Conta dias com marcações por colaborador (distinct operationalDates)
  const daysPerEmployee = new Map<AnonymousId, Set<string>>();
  for (const punch of result.dataset.punches) {
    const set = daysPerEmployee.get(punch.employeeId);
    if (set) {
      set.add(punch.operationalDate);
    } else {
      daysPerEmployee.set(punch.employeeId, new Set([punch.operationalDate]));
    }
  }

  // Determina se a métrica é melhor ordenada por minutos ou por contagem
  const useMinutes =
    metricId === 'absent.short_journeys' ||
    metricId === 'absent.faltas' ||
    metricId === 'cost.excess_minutes' ||
    metricId === 'cost.cost_overtime' ||
    metricId === 'cost.cost_atrasos';

  // cost.cost_absences: ordenar por dias de falta (contagem de eventos — cada evento = 1 dia)
  const useDays = metricId === 'cost.cost_absences';

  const totalLabel = useDays ? 'dias de falta' : useMinutes ? 'minutos totais' : 'ocorrências';

  const rankings: EmployeeRanking[] = [];

  for (const [employeeId, data] of employeeMap) {
    const empIdx = result.dataset.employees.findIndex(e => e.identity.id === employeeId);
    const employee = result.dataset.employees[empIdx];
    const displayName = employee
      ? buildDisplayName(employee, empIdx)
      : `Colaborador ${employeeId.slice(0, 8)}`;

    const daysConsidered = daysPerEmployee.get(employeeId)?.size ?? 0;
    const totalValue = useMinutes ? data.totalMinutes : data.count;

    rankings.push({
      employeeId,
      displayName,
      eventCount: data.count,
      daysConsidered,
      totalValue,
      totalLabel,
    });
  }

  // Ordenar desc por totalValue; desempate por eventCount desc
  rankings.sort((a, b) => {
    if (b.totalValue !== a.totalValue) return b.totalValue - a.totalValue;
    return b.eventCount - a.eventCount;
  });

  return rankings;
}

/**
 * Métricas que ganham view de calendário do período: além dos dias com
 * marcação, também são exibidas linhas pra dias-de-trabalho-da-empresa
 * sem ponto (faltas inferidas), eventos AEJ de ausência e folgas.
 */
const ABSENCE_CALENDAR_METRICS: ReadonlySet<string> = new Set([
  'cost.cost_absences',
  'absent.faltas',
]);

/**
 * Itera de `start` (inclusivo) até `end` (inclusivo), gerando datas YYYY-MM-DD.
 * Usa UTC pra evitar drift por fuso horário.
 */
function* iterateDates(start: LocalDate, end: LocalDate): IterableIterator<LocalDate> {
  const cur = new Date(start + 'T00:00:00Z');
  const last = new Date(end + 'T00:00:00Z');
  while (cur.getTime() <= last.getTime()) {
    yield cur.toISOString().slice(0, 10);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
}

/**
 * Retorna o breakdown diário de um colaborador específico para a métrica solicitada.
 *
 * Para métricas em `ABSENCE_CALENDAR_METRICS` o breakdown traz o calendário
 * completo da janela de presença do colaborador (do primeiro ao último ponto):
 * dias com marcação, faltas inferidas e folgas, todos classificados
 * por `kind`. Pras demais métricas mantém só dias com marcação.
 */
export function getEmployeeDailyBreakdown(
  result: DiagnosticResult,
  metricId: string,
  employeeId: AnonymousId,
  assumptions: DiagnosticAssumptions,
): DailyBreakdown[] {
  const allJourneys = buildDailyJourneys(result.dataset, assumptions);
  const employeeJourneys = allJourneys.filter((j) => j.employeeId === employeeId);

  if (employeeJourneys.length === 0) return [];

  const punchedBreakdowns = employeeJourneys.map((journey) =>
    journeyToBreakdown(journey, result),
  );

  if (!ABSENCE_CALENDAR_METRICS.has(metricId)) {
    punchedBreakdowns.sort((a, b) =>
      a.sessionKey < b.sessionKey ? -1 : a.sessionKey > b.sessionKey ? 1 : 0,
    );
    return punchedBreakdowns;
  }

  // ===== Calendário do período (cobertura completa entre primeiro e último ponto) =====

  const punchedDates = new Set<string>();
  let firstPunch: string | undefined;
  let lastPunch: string | undefined;
  for (const journey of employeeJourneys) {
    if (journey.workedMinutes <= 0) continue;
    punchedDates.add(journey.operationalDate);
    if (firstPunch === undefined || journey.operationalDate < firstPunch) {
      firstPunch = journey.operationalDate;
    }
    if (lastPunch === undefined || journey.operationalDate > lastPunch) {
      lastPunch = journey.operationalDate;
    }
  }

  // Sem marcações reais não dá pra delimitar a janela — devolve só o que tem.
  if (firstPunch === undefined || lastPunch === undefined) {
    punchedBreakdowns.sort((a, b) =>
      a.sessionKey < b.sessionKey ? -1 : a.sessionKey > b.sessionKey ? 1 : 0,
    );
    return punchedBreakdowns;
  }

  const companyWorkDays = computeCompanyWorkDays(allJourneys);
  const aejAbsenceByDate = new Map<string, { minutes: number; reason?: string }>();
  for (const ev of result.dataset.absenceBankEvents) {
    if (ev.kind !== 'absence' || ev.employeeId !== employeeId) continue;
    aejAbsenceByDate.set(ev.date, { minutes: ev.minutes ?? 0, reason: ev.reasonCode });
  }

  const dailyHours = assumptions.dailyHours > 0 ? assumptions.dailyHours : 8;
  const dailyMinutes = dailyHours * 60;

  const breakdowns: DailyBreakdown[] = [...punchedBreakdowns];

  for (const date of iterateDates(firstPunch, lastPunch)) {
    if (punchedDates.has(date)) continue; // já coberto por journey

    const weekdayIdx = weekdayOf(date);
    const weekday = WEEKDAY_NAMES_PT[weekdayIdx] ?? 'desconhecido';

    const aejEvent = aejAbsenceByDate.get(date);
    if (aejEvent) {
      breakdowns.push({
        date,
        sessionKey: `${date}#aej`,
        weekday,
        kind: 'aej-absence',
        punches: [],
        expectedMin: aejEvent.minutes,
        workedMin: 0,
        saldoMin: -aejEvent.minutes,
        hasIssue: true,
        hasIncompletePunches: false,
        incompleteData: false,
        issues: [`Falta registrada no AEJ${aejEvent.reason ? ` (motivo ${aejEvent.reason})` : ''}.`],
      });
      continue;
    }

    const isCompanyWorkDay = companyWorkDays.has(date);
    if (isCompanyWorkDay) {
      breakdowns.push({
        date,
        sessionKey: `${date}#falta`,
        weekday,
        kind: 'inferred-falta',
        punches: [],
        expectedMin: dailyMinutes,
        workedMin: 0,
        saldoMin: -dailyMinutes,
        hasIssue: true,
        hasIncompletePunches: false,
        incompleteData: false,
        issues: ['Dia de trabalho da empresa sem marcação — falta inferida.'],
      });
    } else {
      breakdowns.push({
        date,
        sessionKey: `${date}#folga`,
        weekday,
        kind: 'folga',
        punches: [],
        expectedMin: 0,
        workedMin: 0,
        saldoMin: 0,
        hasIssue: false,
        hasIncompletePunches: false,
        incompleteData: false,
        issues: [],
      });
    }
  }

  breakdowns.sort((a, b) =>
    a.sessionKey < b.sessionKey ? -1 : a.sessionKey > b.sessionKey ? 1 : 0,
  );

  return breakdowns;
}

/** Converte uma DailyJourney em DailyBreakdown (linha de calendário com kind='punched'). */
function journeyToBreakdown(
  journey: DailyJourney,
  result: DiagnosticResult,
): DailyBreakdown {
  const sessionPunches: typeof result.dataset.punches[number][] = [];
  for (const pair of journey.pairs) {
    if (pair.entry) sessionPunches.push(pair.entry);
    if (pair.exit) sessionPunches.push(pair.exit);
  }
  sessionPunches.sort((a, b) =>
    a.dateTime < b.dateTime ? -1 : a.dateTime > b.dateTime ? 1 : 0,
  );

  const punchesDisplay = sessionPunches.map((p, idx) => ({
    time: p.dateTime.length >= 16 ? p.dateTime.slice(11, 16) : p.dateTime,
    kind: classifyPunchKind(idx),
  }));

  const expectedMin = journey.expectedMinutes;
  const workedMin = journey.workedMinutes;
  const hasIncompletePunches = journey.pairs.some((p) => p.imbalanced);
  const incompleteData = journey.incompleteData;
  // Marcações ímpares OU dados parciais = saldo não é calculável (exibido como "—").
  const saldoMin: number | null =
    hasIncompletePunches || incompleteData ? null : workedMin - expectedMin;
  const issues = journey.issues.map((i) => i.message);
  const hasIssue = issues.length > 0 || hasIncompletePunches || incompleteData;

  const weekdayIdx = weekdayOf(journey.operationalDate);
  const weekday = WEEKDAY_NAMES_PT[weekdayIdx] ?? 'desconhecido';

  const firstPunchDateTime = sessionPunches[0]?.dateTime ?? journey.operationalDate;

  return {
    date: journey.operationalDate,
    sessionKey: `${journey.operationalDate}#${firstPunchDateTime}`,
    weekday,
    kind: 'punched',
    punches: punchesDisplay,
    expectedMin,
    workedMin,
    saldoMin,
    hasIssue,
    hasIncompletePunches,
    incompleteData,
    issues,
  };
}

/**
 * Retorna a jornada diária inferida (em minutos) para o colaborador, ou `null`
 * quando não há sinal forte ou quando o dataset tem AEJ real (nesse caso a
 * escala canônica já dá a resposta correta por weekday).
 *
 * Útil pra UI exibir "Jornada inferida: 6h/dia" no header do drill-down,
 * dando ao consultor visibilidade sobre o número que alimenta o saldo do dia.
 */
export function getInferredEmployeeJourneyMinutes(
  result: DiagnosticResult,
  employeeId: AnonymousId,
): number | null {
  const isSynthetic =
    result.dataset.sources?.schedulesAreSynthetic === true ||
    result.dataset.schedules.length === 0;
  if (!isSynthetic) return null;
  return inferEmployeeDailyMinutes(result.dataset.punches, employeeId);
}

// =========================================================================
// Metadados de coluna por metricId
// =========================================================================

export interface ColumnDef {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
}

export interface DrillDownMeta {
  title: string;
  columns: ColumnDef[];
}

const DRILL_DOWN_META: Record<string, DrillDownMeta> = {
  'absent.short_journeys': {
    title: 'Jornadas mais curtas que a escala',
    columns: [
      { key: 'employeeLabel', label: 'Colaborador' },
      { key: 'date', label: 'Data' },
      { key: 'minutes', label: 'Déficit (min)', align: 'right' },
      { key: 'expectedMinutes', label: 'Esperado (min)', align: 'right' },
      { key: 'workedMinutes', label: 'Trabalhado (min)', align: 'right' },
    ],
  },
  'absent.faltas': {
    title: 'Faltas (inferidas)',
    columns: [
      { key: 'employeeLabel', label: 'Colaborador' },
      { key: 'date', label: 'Data' },
      { key: 'source', label: 'Origem' },
    ],
  },
  'cost.excess_minutes': {
    title: 'Total de minutos acima da jornada prevista',
    columns: [
      { key: 'employeeLabel', label: 'Colaborador' },
      { key: 'date', label: 'Data' },
      { key: 'minutes', label: 'Excesso (min)', align: 'right' },
      { key: 'expectedMinutes', label: 'Esperado (min)', align: 'right' },
      { key: 'workedMinutes', label: 'Trabalhado (min)', align: 'right' },
    ],
  },
  'cost.cost_overtime': {
    title: 'Custo das horas extras',
    columns: [
      { key: 'employeeLabel', label: 'Colaborador' },
      { key: 'date', label: 'Data' },
      { key: 'minutes', label: 'Excesso (min)', align: 'right' },
      { key: 'expectedMinutes', label: 'Esperado (min)', align: 'right' },
      { key: 'workedMinutes', label: 'Trabalhado (min)', align: 'right' },
    ],
  },
  'cost.cost_atrasos': {
    title: 'Custo das jornadas mais curtas',
    columns: [
      { key: 'employeeLabel', label: 'Colaborador' },
      { key: 'date', label: 'Data' },
      { key: 'minutes', label: 'Déficit (min)', align: 'right' },
      { key: 'expectedMinutes', label: 'Esperado (min)', align: 'right' },
      { key: 'workedMinutes', label: 'Trabalhado (min)', align: 'right' },
    ],
  },
  'cost.cost_absences': {
    title: 'Custo das faltas (inferidas)',
    columns: [
      { key: 'employeeLabel', label: 'Colaborador' },
      { key: 'date', label: 'Data' },
      { key: 'days', label: 'Dias', align: 'right' },
      { key: 'source', label: 'Origem' },
    ],
  },
};

export function getDrillDownMeta(metricId: string): DrillDownMeta | null {
  return DRILL_DOWN_META[metricId] ?? null;
}
